<script setup lang="ts">
import { storeToRefs } from "pinia";
import { computed, ref } from "vue";

import { EDashboardType } from "@/app/data/dashboard/dashboard.types";
import { useDashboardDateRange } from "@/composables/dashboard/use-dashboard-date-range";
import { useGetSitesQuery } from "@/composables/kili/sites/queries/use-get-sites-query";
import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import {
  TOrganizationPerimeter,
  TSitePerimeter,
} from "@/data/api/kili/consumption/types/user-organization-perimeter.type";
import { useTypedIntl } from "@/locales/typed-intl";
import { useDashboardStore } from "@/stores/dashboard/dashboard-store";

import DashboardMono from "@/app/data/dashboard/dashboard-mono.vue";
import DashboardMulti from "@/app/data/dashboard/dashboard-multi.vue";
import SelectPerimeterButton from "@/components/data/dashboard/select-perimeter/select-perimeter-button.vue";
import SelectPerimeterDialog from "@/components/data/dashboard/select-perimeter/select-perimeter-dialog.vue";
import BaseLayout from "@/components/layout/base-layout.vue";
import BaseTitle from "@/components/layout/base-title.vue";
import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";

const { TO_DAY, FROM_DAY } = useDashboardDateRange();

const { t, k, d } = useTypedIntl();

const dashboardStore = useDashboardStore();
const { organizationPerimeter, sitePerimeter } = storeToRefs(dashboardStore);

const { data: sites, isLoading } = useGetSitesQuery();

const isSelectPerimeterDialogVisible = ref<boolean>(false);

const maybeMonosite = computed<TSitePerimeter | undefined>(() => {
  // There are two ways to get monosite dashboard: either the user only has access to one site
  if (sites.value?.length === 1) {
    return {
      siteId: sites.value[0].siteId,
      siteName: sites.value[0].name,
    } satisfies TSitePerimeter;
  }

  // or the user has access to multiple sites and selects an organization with one site
  if (!sitePerimeter.value || sitePerimeter.value.length > 1) return;
  return sitePerimeter.value[0];
});

async function handleSelectPerimeter(selection: TOrganizationPerimeter[]) {
  dashboardStore.setOrganizationPerimeter(selection);
  isSelectPerimeterDialogVisible.value = false;
}

async function handleResetOrganizationPerimeter() {
  dashboardStore.setOrganizationPerimeter(null);
}
</script>

<template>
  <BaseLayout :is-loading="isLoading">
    <div
      v-if="!sites?.length"
      class="w-full h-full flex justify-center items-center"
    >
      <span class="text-text-secondary">
        {{ t(k.data.dashboard.emptySitePerimeter) }}
      </span>
    </div>

    <template v-else>
      <BaseTitle>
        <template #title>
          <div class="flex gap-2 items-center">
            <h3>
              {{ t(k.data.dashboard.title) }}
            </h3>

            <h5
              v-if="sites?.length === 1"
              class="text-brand"
            >
              {{ maybeMonosite?.siteName }}
            </h5>

            <HospitalityButton
              v-else-if="!organizationPerimeter"
              class="hover:!bg-background-blur/10 !px-4"
              :data-test-id="DATA_TEST_IDS.views.dashboard.selectPerimeter.button"
              :label="t(k.global.allSites)"
              severity="tertiary"
              size="sm"
              @click="isSelectPerimeterDialogVisible = true"
            />

            <SelectPerimeterButton
              v-else
              :badge-value="organizationPerimeter.length"
              @clear="handleResetOrganizationPerimeter"
              @click="isSelectPerimeterDialogVisible = true"
            />
          </div>
        </template>
        <template #subtitle>
          <span class="text-text-secondary">
            {{
              t(
                k.data.dashboard.subtitle[maybeMonosite ? EDashboardType.MONOSITE : EDashboardType.MULTISITE]({
                  fromDate: d(FROM_DAY),
                  toDate: d(TO_DAY),
                }),
              )
            }}
          </span>
        </template>
      </BaseTitle>

      <DashboardMono
        v-if="maybeMonosite"
        :from-day="FROM_DAY"
        :site-id="maybeMonosite.siteId"
        :to-day="TO_DAY"
      />

      <DashboardMulti
        v-else
        :from-day="FROM_DAY"
        :to-day="TO_DAY"
      />
    </template>
  </BaseLayout>

  <SelectPerimeterDialog
    v-model:visible="isSelectPerimeterDialogVisible"
    @submit="handleSelectPerimeter($event)"
  />
</template>
